import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MsalErrorHandlerService } from './shared/services/msal-error-handler.service';

const TOKEN_SUCCESS_EVENTS: EventType[] = [
  EventType.LOGIN_SUCCESS,
  EventType.ACQUIRE_TOKEN_SUCCESS,
  EventType.SSO_SILENT_SUCCESS,
  EventType.HANDLE_REDIRECT_END,
];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'valamar-budgeting-and-pricing-spa';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private msalErrorHandler: MsalErrorHandlerService,
    private router: Router,
    private msalBroadcastService: MsalBroadcastService,
    private msalService: MsalService,
  ) {}

  ngOnInit(): void {
    this.logUrlChanges();
    this.logMsalTokenEvents();
    this.keepActiveAccountFresh();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private logUrlChanges(): void {
    // Log the current URL on load, then every subsequent navigation.
   // console.log('[URL] initial:', this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
       // console.log('[URL] changed:', event.urlAfterRedirects);
      });
  }

  private logMsalTokenEvents(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((message: EventMessage) => TOKEN_SUCCESS_EVENTS.includes(message.eventType)),
        takeUntil(this.destroy$),
      )
      .subscribe((message: EventMessage) => {
        const result = message.payload as AuthenticationResult | null;

        // console.log('[MSAL] event:', message.eventType, {
        //   account: result?.account?.username,
        //   scopes: result?.scopes,
        //   expiresOn: result?.expiresOn,
        //   accessToken: result?.accessToken,
        //   idToken: result?.idToken,
        // });
      });
  }

  /**
   * Re-derives MSAL's active account every time an interaction settles
   * (redirect handled, login/token acquisition finished, etc.), not just when
   * a route guard happens to run. Prefers the account MSAL already considers
   * active; falls back to the first cached account and explicitly sets it as
   * active otherwise. Mirrors the fix that stabilized the same login flow in
   * the ContentRocket app, where unconditionally trusting getAllAccounts()[0]
   * (or never re-deriving it after inactivity/cache changes) left users stuck.
   */
  private keepActiveAccountFresh(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        const activeAccount = this.msalService.instance.getActiveAccount();
        if (!activeAccount) {
          const [firstAccount] = this.msalService.instance.getAllAccounts();
          if (firstAccount) {
            this.msalService.instance.setActiveAccount(firstAccount);
          }
        }
      });
  }
}
